import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import logo from "../assets/logoj.svg";

const navLinks = [
  { label: "Pricing", href: "/pricing" },
  { label: "Support", href: "/support" },
  { label: "Contact", href: "/contacts" },

  {
    label: "Documentation",
    dropdown: [{ label: "Integration", href: "/doc" }],
  },
];

const dropdownVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const Navbar = ({ mainRef }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    const node = mainRef?.current;
    if (!node) return;

    const handleScroll = () => {
      setAtTop(node.scrollTop === 0);
    };

    node.addEventListener("scroll", handleScroll);
    handleScroll(); // initial check

    return () => node.removeEventListener("scroll", handleScroll);
  }, [mainRef]);

  const handleDropdown = (label) => {
    setActiveDropdown((prev) => (prev === label ? null : label));
  };

  const isAuthenticated =
    useSelector((state) => state.auth.isAuthenticated) || false;

  return (
    <motion.header
      className="sticky top-0 z-50 border-b border-gray-100"
      style={{
        backgroundColor: atTop ? "white" : "rgba(255,255,255,0.95)",
        boxShadow: atTop ? "0 1px 2px rgba(0,0,0,0.05)" : "none", // similar to shadow-sm
      }}
      whileHover={{
        backgroundColor: "white",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)", // on hover, show shadow
      }}
      transition={{ duration: 0.2 }}>
      <nav className="flex items-center justify-between px-4 sm:px-10 py-2 text-black">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img src={logo} alt="Janoo logo" className="h-8 w-auto" />
        </a>

        {/* Nav Links */}
        <ul className="hidden md:flex space-x-8 items-center text-sm font-medium">
          {navLinks.map((link, idx) => (
            <li key={idx} className="relative">
              {!link.dropdown ? (
                <a href={link.href} className="hover:text-blue-600">
                  {link.label}
                </a>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => handleDropdown(link.label)}
                    className="hover:text-blue-600 focus:outline-none">
                    {link.label}
                  </button>
                  <AnimatePresence>
                    {activeDropdown === link.label && (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={dropdownVariants}
                        className="absolute mt-2 bg-white border rounded shadow-lg min-w-[140px] z-10">
                        {link.dropdown.map((item, subIdx) => (
                          <a
                            key={subIdx}
                            href={item.href}
                            className="block px-4 py-2 hover:bg-gray-100">
                            {item.label}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Login/CTA */}
        {isAuthenticated ? (
          <div className="flex items-center space-x-4 text-sm font-medium">
            <motion.a
              whileTap={{ scale: 0.9 }}
              href="/dashboard"
              className="bg-black hover,:bg-darkprimary  text-white px-4 py-2 rounded">
              Dashboard
            </motion.a>
          </div>
        ) : (
          <div className="flex items-center space-x-4 text-sm font-medium">
            <a href="/login" className="hover:text-blue-600">
              Login
            </a>
            <a
              href="/signup"
              className="hidden md:flex bg-darkprimary text-white px-4 py-2 rounded-md hover:bg-primarydark transition">
              Try for free
            </a>
            <a
              href="/pricing"
              className="md:hidden bg-darkprimary text-white px-4 py-2 rounded-md hover:bg-primarydark transition">
              Pricing
            </a>
          </div>
        )}
      </nav>
    </motion.header>
  );
};

export default Navbar;
